import { DEFAULT_LOCALE, fallbackChain } from './locale-catalog';
import { pluralCategory } from './formatting';

/**
 * Translation resources: locale code -> flat map of dotted key to string.
 *
 * Kept flat rather than nested so a missing key is a single failed lookup
 * instead of a walk that can bail out halfway through.
 */
export type TranslationResources = Record<string, Record<string, string>>;

export interface TranslateOptions {
  /** Interpolated into `{{placeholder}}` slots. */
  readonly values?: Record<string, string | number>;
  /** Selects a plural form; the key is suffixed with `_one`, `_other`, etc. */
  readonly count?: number;
  /** Returned when the key is missing in every locale in the chain. */
  readonly fallback?: string;
}

/**
 * The runtime translator, shared by server and browser.
 *
 * A missing key is reported through `onMissingKey` rather than thrown: a
 * half-translated screen is recoverable, a crashed one is not. In development
 * that callback logs loudly; in CI it fails the build.
 */
export class Translator {
  constructor(
    private readonly resources: TranslationResources,
    private readonly locale: string = DEFAULT_LOCALE,
    private readonly onMissingKey?: (key: string, locale: string) => void,
  ) {}

  withLocale(locale: string): Translator {
    return new Translator(this.resources, locale, this.onMissingKey);
  }

  get currentLocale(): string {
    return this.locale;
  }

  t(key: string, options: TranslateOptions = {}): string {
    const lookupKey =
      options.count === undefined ? key : `${key}_${pluralCategory(options.count, this.locale)}`;

    const template =
      this.lookup(lookupKey) ??
      // A locale that lacks the specific plural form still has `_other`.
      (options.count !== undefined ? this.lookup(`${key}_other`) : undefined) ??
      this.lookup(key);

    if (template === undefined) {
      this.onMissingKey?.(key, this.locale);
      return options.fallback ?? key;
    }

    return this.interpolate(template, {
      ...options.values,
      ...(options.count !== undefined ? { count: options.count } : {}),
    });
  }

  has(key: string): boolean {
    return this.lookup(key) !== undefined;
  }

  private lookup(key: string): string | undefined {
    for (const locale of fallbackChain(this.locale)) {
      const value = this.resources[locale]?.[key];
      if (value !== undefined) return value;
    }
    return undefined;
  }

  private interpolate(template: string, values: Record<string, string | number>): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
      const value = values[name];
      return value === undefined ? match : String(value);
    });
  }
}

/**
 * Assembles the resource map a Translator needs: the active locale, with
 * English beneath it as the end of the fallback chain.
 *
 * The guard on `locale === DEFAULT_LOCALE` is the entire reason this is a named
 * function rather than an object literal at the call site. Written inline as
 * `{ [locale]: active, [DEFAULT_LOCALE]: fallback }`, the two computed keys
 * collide when the active locale *is* English, and the later one wins — so
 * English loads an empty bundle and every screen renders raw keys like
 * `auth.signIn`. Nothing fails; the other nine locales are unaffected, which is
 * why it survived review and a passing build.
 */
export function buildResourceBundle(
  locale: string,
  active: Record<string, string>,
  fallback: Record<string, string>,
): TranslationResources {
  if (locale === DEFAULT_LOCALE) return { [DEFAULT_LOCALE]: active };
  return { [locale]: active, [DEFAULT_LOCALE]: fallback };
}

/** Flattens a nested resource object into the dotted keys the translator uses. */
export function flattenResources(
  nested: Record<string, unknown>,
  prefix = '',
): Record<string, string> {
  const flat: Record<string, string> = {};

  for (const [key, value] of Object.entries(nested)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      flat[path] = value;
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(flat, flattenResources(value as Record<string, unknown>, path));
    }
  }

  return flat;
}
